import React, { useRef, useEffect, useCallback } from 'react';

/**
 * ExpandableInput - Auto-expanding text input
 * 
 * A text input that automatically grows vertically as the user types,
 * allowing full visibility of all entered text without scrolling.
 * 
 * DEX Standard: Premium minimalism, seamless UX
 * 
 * @param {string} value - Controlled value
 * @param {function} onChange - Value change handler
 * @param {string} placeholder - Placeholder text
 * @param {number} minRows - Minimum number of rows (default: 1)
 * @param {number} maxRows - Maximum number of rows before scrolling (default: 6)
 * @param {string} className - Additional CSS classes
 * @param {React.ReactNode} leftIcon - Optional icon to display on the left
 * @param {boolean} disabled - Disable input
 * @param {function} onKeyDown - Key down handler
 * @param {function} onFocus - Focus handler
 * @param {function} onBlur - Blur handler
 */
export default function ExpandableInput({
  value = '',
  onChange,
  placeholder = '',
  minRows = 1,
  maxRows = 6,
  className = '',
  leftIcon,
  disabled = false,
  onKeyDown,
  onFocus,
  onBlur,
  autoFocus = false,
}) {
  const textareaRef = useRef(null);
  const hiddenRef = useRef(null);
  
  // Calculate and set the height based on content
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    const hidden = hiddenRef.current;
    if (!textarea || !hidden) return;
    
    // Get computed styles for accurate measurement
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseInt(computedStyle.lineHeight) || 20;
    const paddingTop = parseInt(computedStyle.paddingTop) || 0;
    const paddingBottom = parseInt(computedStyle.paddingBottom) || 0;
    
    // Calculate min and max heights
    const minHeight = (lineHeight * minRows) + paddingTop + paddingBottom;
    const maxHeight = (lineHeight * maxRows) + paddingTop + paddingBottom;
    
    // Reset height to auto to get the scroll height
    textarea.style.height = 'auto';
    
    // Use hidden div for accurate content measurement
    hidden.textContent = value || placeholder || 'X';
    const contentHeight = hidden.scrollHeight;
    
    // Calculate new height within bounds
    const newHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;
    
    // Enable scrolling only if content exceeds max height
    textarea.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
  }, [value, placeholder, minRows, maxRows]);
  
  // Adjust height on value change
  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);
  
  // Adjust height on window resize
  useEffect(() => {
    window.addEventListener('resize', adjustHeight);
    return () => window.removeEventListener('resize', adjustHeight);
  }, [adjustHeight]);
  
  // Handle input change
  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };
  
  // Handle key down - allow Shift+Enter for new lines
  const handleKeyDown = (e) => {
    // If Enter without Shift, let parent handle it (usually submit)
    if (e.key === 'Enter' && !e.shiftKey && onKeyDown) {
      onKeyDown(e);
    } else if (onKeyDown) {
      onKeyDown(e);
    }
  };
  
  return (
    <div className={`relative flex items-start gap-2 ${className}`}>
      {/* Left icon */}
      {leftIcon && (
        <div className="flex-shrink-0 mt-2">
          {leftIcon}
        </div>
      )}
      
      {/* Hidden div for measuring content */}
      <div
        ref={hiddenRef}
        aria-hidden="true"
        className="absolute invisible whitespace-pre-wrap break-words"
        style={{
          position: 'absolute',
          visibility: 'hidden',
          height: 'auto',
          width: textareaRef.current?.offsetWidth || '100%',
          padding: '8px 12px',
          fontSize: '14px',
          lineHeight: '20px',
          fontFamily: 'inherit',
        }}
      />
      
      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        rows={minRows}
        className={`
          flex-1 bg-transparent text-black placeholder-black/30 
          focus:outline-none text-sm leading-5 resize-none
          transition-all duration-200
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        style={{
          minHeight: `${minRows * 20 + 16}px`, // lineHeight * rows + padding
          overflowY: 'hidden',
        }}
      />
    </div>
  );
}
